import { useMemo, useState } from "react";
import { ComposableMap, Geographies, Geography, Marker } from "react-simple-maps";
import { MapPin, Clock } from "lucide-react";
import { useLaunchState } from "@/components/Countdown";

/**
 * USFarmMap — interactive US map with pins for each partner farm.
 * Uses react-simple-maps + us-atlas topojson from jsdelivr (cached, ~40KB).
 * Includes protein filter chips (Beef / Seafood / Poultry / Pork / All).
 * Farm names are BLINDED to "Announcing soon" until contracts sign.
 */

// us-atlas 50m states topojson — public CDN, cached at the edge.
const GEO_URL =
  "https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json";

// Protein taxonomy — maps user-facing chip → animal keywords in vendor.animals_raised.
const FILTER_CHIPS = [
  { id: "all", label: "All", match: null },
  { id: "beef", label: "Beef", match: ["cattle", "beef", "bison", "steer"] },
  { id: "chicken", label: "Chicken", match: ["poultry", "chicken", "turkey", "duck"] },
];

function farmMatchesFilter(farm, filterId) {
  if (filterId === "all") return true;
  const chip = FILTER_CHIPS.find(c => c.id === filterId);
  if (!chip || !chip.match) return true;
  const animals = (farm.animals_raised || []).map(a => String(a).toLowerCase());
  return animals.some(a => chip.match.some(kw => a.includes(kw)));
}

export default function USFarmMap({ farms, testId = "us-farm-map" }) {
  const [hoveredId, setHoveredId] = useState(null);
  const [filter, setFilter] = useState("all");
  const launch = useLaunchState();
  // Compute "reveal in N days" — anchored to launch_at (public drops go live).
  const revealDaysLeft = useMemo(() => {
    const at = launch?.launch_at;
    if (!at) return null;
    const ms = new Date(at).getTime() - Date.now();
    if (ms <= 0) return 0;
    return Math.ceil(ms / 86400000);
  }, [launch]);

  const allPins = useMemo(() => {
    if (!Array.isArray(farms)) return [];
    return farms.filter(f =>
      typeof f.latitude === "number" &&
      typeof f.longitude === "number" &&
      f.latitude !== 0 &&
      f.longitude !== 0
    );
  }, [farms]);

  const pins = useMemo(
    () => allPins.filter(f => farmMatchesFilter(f, filter)),
    [allPins, filter]
  );

  // Precompute which chips have at least one match — hide empty ones so the
  // user doesn't tap into an empty state.
  const availableChips = useMemo(() => {
    return FILTER_CHIPS.filter(c =>
      c.id === "all" || allPins.some(f => farmMatchesFilter(f, c.id))
    );
  }, [allPins]);

  return (
    <div data-testid={testId}>
      {/* Filter chip row */}
      {availableChips.length > 1 && (
        <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="us-map-filters">
          <span className="text-xs uppercase tracking-wider text-[var(--ff-text-muted)] mr-1 font-semibold">Filter:</span>
          {availableChips.map(c => {
            const active = c.id === filter;
            const count = c.id === "all" ? allPins.length : allPins.filter(f => farmMatchesFilter(f, c.id)).length;
            return (
              <button
                key={c.id}
                type="button"
                onClick={() => setFilter(c.id)}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wider transition-colors ${
                  active
                    ? "bg-[var(--ff-primary)] text-white border border-transparent"
                    : "bg-white text-[var(--ff-primary)] border border-[var(--ff-border)] hover:border-[var(--ff-primary)]"
                }`}
                data-testid={`us-map-filter-${c.id}`}
                aria-pressed={active}
              >
                {c.label}
                <span className={`text-[10px] tabular-nums ${active ? "opacity-80" : "text-[var(--ff-text-muted)]"}`}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>
      )}

      <div className="relative rounded-2xl border border-[var(--ff-border)] bg-white overflow-hidden">
        <ComposableMap
          projection="geoAlbersUsa"
          projectionConfig={{ scale: 1000 }}
          width={960}
          height={520}
          style={{ width: "100%", height: "auto", background: "var(--ff-cream)" }}
        >
          <Geographies geography={GEO_URL}>
            {({ geographies }) =>
              geographies.map(geo => (
                <Geography
                  key={geo.rsmKey}
                  geography={geo}
                  fill="var(--ff-muted)"
                  stroke="var(--ff-primary)"
                  strokeWidth={0.6}
                  style={{
                    default: { outline: "none" },
                    hover: { fill: "var(--ff-accent-kraft)", outline: "none", transition: "fill 200ms" },
                    pressed: { outline: "none" },
                  }}
                />
              ))
            }
          </Geographies>

          {pins.map(p => (
            <Marker
              key={p.id}
              coordinates={[p.longitude, p.latitude]}
              onMouseEnter={() => setHoveredId(p.id)}
              onMouseLeave={() => setHoveredId(null)}
              data-testid={`farm-pin-${p.id}`}
            >
              {/* Pulse ring */}
              <circle r={16} fill="var(--ff-citrus)" opacity={0.18}>
                <animate attributeName="r" values="12;20;12" dur="2.4s" repeatCount="indefinite" />
                <animate attributeName="opacity" values="0.28;0.05;0.28" dur="2.4s" repeatCount="indefinite" />
              </circle>
              <circle r={7} fill="var(--ff-citrus)" stroke="#fff" strokeWidth={2.5} />
              <circle r={2.5} fill="#fff" />
              {/* Placeholder label — real farm names revealed when contracts close */}
              <text
                y={-14}
                textAnchor="middle"
                style={{
                  fontFamily: "'Fraunces', serif",
                  fontSize: 11,
                  fontWeight: 600,
                  fill: "var(--ff-primary)",
                  paintOrder: "stroke",
                  stroke: "#fff",
                  strokeWidth: 3,
                  strokeLinejoin: "round",
                  letterSpacing: "0.05em",
                  textTransform: "uppercase",
                }}
              >
                Announcing soon
              </text>
            </Marker>
          ))}
        </ComposableMap>

        {/* Empty state overlay */}
        {pins.length === 0 && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none" data-testid="us-map-empty">
            <div className="bg-white/95 backdrop-blur border border-[var(--ff-border)] rounded-lg px-5 py-4 text-center shadow-sm">
              <p className="font-display text-lg text-[var(--ff-primary)]">No partners in this filter yet.</p>
              <p className="text-xs text-[var(--ff-text-muted)] mt-1">Announcing new farms as contracts close.</p>
            </div>
          </div>
        )}

        {/* Farm cards under the map — names blinded until contracts sign */}
        <div className="border-t border-[var(--ff-border)] bg-white p-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="us-map-farm-list">
          {pins.map(p => (
            <div
              key={p.id}
              className={`flex items-start gap-3 rounded-lg border p-3 transition-colors ${
                hoveredId === p.id
                  ? "border-[var(--ff-primary)] bg-[var(--ff-primary)]/5"
                  : "border-[var(--ff-border)]"
              }`}
              onMouseEnter={() => setHoveredId(p.id)}
              onMouseLeave={() => setHoveredId(null)}
              data-testid={`farm-list-item-${p.id}`}
            >
              <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-[var(--ff-citrus)]/15 text-[var(--ff-citrus)] flex-shrink-0 mt-0.5">
                <MapPin size={14} />
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <p className="font-semibold text-sm text-[var(--ff-primary)] truncate">Announcing soon</p>
                  {revealDaysLeft !== null && revealDaysLeft > 0 && (
                    <span
                      className="inline-flex items-center gap-1 bg-[var(--ff-accent-kraft)]/40 text-[var(--ff-primary)] text-[10px] font-semibold uppercase tracking-wider rounded-full px-2 py-0.5 flex-shrink-0"
                      data-testid={`farm-reveal-timer-${p.id}`}
                    >
                      <Clock size={9}/> Reveal in {revealDaysLeft}d
                    </span>
                  )}
                </div>
                <p className="text-xs text-[var(--ff-text-muted)] mt-0.5">{p.location}</p>
                {Array.isArray(p.animals_raised) && p.animals_raised.length > 0 && (
                  <p className="text-[10px] text-[var(--ff-text-muted)] mt-1 uppercase tracking-wider">
                    {p.animals_raised.slice(0, 3).join(" · ")}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="absolute top-3 right-3 flex items-center gap-2 bg-white/90 backdrop-blur rounded-md border border-[var(--ff-border)] px-3 py-1.5 text-xs text-[var(--ff-text-secondary)]" data-testid="us-map-count">
          <span className="w-2 h-2 rounded-full bg-[var(--ff-citrus)]" /> {pins.length} showing · {allPins.length} total
        </div>
      </div>
    </div>
  );
}
